import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, LinearProgress, Typography } from '@mui/material';
import api from '../api';
import { extractYoutubeVideoId } from '../utils/youtube';

interface VideoProgress {
  watchedSeconds: number;
  durationSeconds: number;
  completed: boolean;
}

interface YouTubePlayer {
  getCurrentTime: () => number;
  getDuration: () => number;
  destroy: () => void;
  getIframe: () => HTMLIFrameElement;
}

interface YouTubePlayerApi {
  PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
  Player: new (element: HTMLElement, options: Record<string, unknown>) => YouTubePlayer;
}

declare global {
  interface Window {
    YT?: YouTubePlayerApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YouTubeLessonPlayerProps {
  examType: 'WAEC' | 'JAMB';
  topicUUID: string;
  lessonIndex: number;
  videoUrl: string;
  title?: string;
}

let youtubeApiPromise: Promise<void> | null = null;

const loadYouTubeApi = (): Promise<void> => {
  if (window.YT?.Player) return Promise.resolve();
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve, reject) => {
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      resolve();
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => {
      youtubeApiPromise = null;
      reject(new Error('The YouTube player could not be loaded.'));
    };
    document.head.appendChild(script);
  });

  return youtubeApiPromise;
};

const formatTime = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
    : `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

const YouTubeLessonPlayer: React.FC<YouTubeLessonPlayerProps> = ({ examType, topicUUID, lessonIndex, videoUrl, title = 'Lesson video' }) => {
  const playerElementRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YouTubePlayer | null>(null);
  const saveInProgressRef = useRef(false);
  const pendingSaveRef = useRef<{ watchedSeconds: number; durationSeconds: number; completed: boolean } | null>(null);
  const [progress, setProgress] = useState<VideoProgress>({ watchedSeconds: 0, durationSeconds: 0, completed: false });
  const [playerError, setPlayerError] = useState('');
  const [progressError, setProgressError] = useState('');
  const [isReady, setIsReady] = useState(false);
  const videoId = extractYoutubeVideoId(videoUrl);

  useEffect(() => {
    let mounted = true;
    let progressTimer: number | null = null;

    const saveProgress = async (completed = false) => {
      const player = playerRef.current;
      if (!mounted || !player) return;

      const durationSeconds = player.getDuration();
      if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return;
      const currentSave = {
        watchedSeconds: Math.max(0, Math.floor(player.getCurrentTime())),
        durationSeconds,
        completed,
      };
      if (saveInProgressRef.current) {
        pendingSaveRef.current = {
          ...currentSave,
          completed: currentSave.completed || Boolean(pendingSaveRef.current?.completed),
        };
        return;
      }

      saveInProgressRef.current = true;
      let save: typeof currentSave | null = currentSave;
      try {
        while (save && mounted) {
          try {
            const response = await api.put('/lesson-video-progress', {
              examType,
              topicUUID,
              lessonIndex,
              ...save,
            });
            if (mounted) {
              setProgress(response.data?.data || save);
              setProgressError('');
            }
          } catch {
            if (mounted) setProgressError('Video progress could not be saved. Playback is still available.');
          }
          save = pendingSaveRef.current;
          pendingSaveRef.current = null;
        }
      } finally {
        saveInProgressRef.current = false;
      }
    };

    const initializePlayer = async () => {
      let resumeSeconds = 0;
      try {
        const response = await api.get('/lesson-video-progress', { params: { examType, topicUUID, lessonIndex } });
        const saved = response.data?.data;
        if (saved && mounted) {
          resumeSeconds = Number(saved.watchedSeconds) || 0;
          setProgress({
            watchedSeconds: resumeSeconds,
            durationSeconds: Number(saved.durationSeconds) || 0,
            completed: Boolean(saved.completed),
          });
        }
      } catch {
        if (mounted) setProgressError('Saved progress could not be loaded. You can still watch this lesson.');
      }

      try {
        await loadYouTubeApi();
        if (!mounted || !playerElementRef.current || !window.YT) return;
        playerRef.current = new window.YT.Player(playerElementRef.current, {
          host: 'https://www.youtube-nocookie.com',
          videoId,
          playerVars: {
            enablejsapi: 1,
            origin: window.location.origin,
            rel: 0,
            start: Math.floor(resumeSeconds),
          },
          events: {
            onReady: (event: { target: YouTubePlayer }) => {
              if (!mounted) return;
              event.target.getIframe().title = 'YouTube lesson video player';
              setIsReady(true);
            },
            onStateChange: (event: { data: number }) => {
              if (!window.YT || !mounted) return;
              if (event.data === window.YT.PlayerState.PLAYING && progressTimer === null) {
                progressTimer = window.setInterval(() => { void saveProgress(); }, 10000);
              } else if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
                if (progressTimer !== null) {
                  window.clearInterval(progressTimer);
                  progressTimer = null;
                }
                void saveProgress(event.data === window.YT.PlayerState.ENDED);
              }
            },
            onError: () => {
              if (mounted) setPlayerError('This YouTube video is unavailable or cannot be embedded.');
            },
          },
        });
      } catch {
        if (mounted) setPlayerError('The YouTube player could not be loaded. Check your connection and try again.');
      }
    };

    if (videoId) void initializePlayer();
    else setPlayerError('This lesson has an invalid YouTube video link.');

    return () => {
      mounted = false;
      if (progressTimer !== null) window.clearInterval(progressTimer);
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [examType, topicUUID, lessonIndex, videoId]);

  if (!videoId) return <Alert severity="error">{playerError}</Alert>;

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      {playerError && <Alert severity="error" sx={{ mb: 1 }}>{playerError}</Alert>}
      {progressError && <Alert severity="warning" sx={{ mb: 1 }}>{progressError}</Alert>}
      {!isReady && !playerError && <LinearProgress sx={{ mb: 1 }} aria-label="Loading lesson video" />}
      <Box sx={{ width: '100%', maxWidth: 800, aspectRatio: '16 / 9', bgcolor: '#000', borderRadius: 2, overflow: 'hidden' }}>
        <Box ref={playerElementRef} sx={{ width: '100%', height: '100%' }} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} aria-live="polite">
        {progress.completed
          ? 'Video completed'
          : progress.watchedSeconds > 0
            ? `Resume point saved at ${formatTime(progress.watchedSeconds)}`
            : 'Your video progress saves automatically as you watch.'}
      </Typography>
    </Box>
  );
};

export default YouTubeLessonPlayer;
